import { useState, useEffect, useCallback } from "react";
import { DatePicker, Table, Tag, Spin } from "antd";
import ReactApexChart from "react-apexcharts";
import dayjs from "dayjs";
import api from "../../../core/axios/axiosInstance";
import {
  IoBusinessOutline,
  IoPersonAddOutline,
  IoCardOutline,
  IoWalletOutline,
  IoAlertCircleOutline,
  IoCheckmarkCircleOutline,
  IoTimeOutline,
  IoCloseCircleOutline,
} from "react-icons/io5";
import "./SuperAdminDashboard.css";
import "../dashboard/Dashboard.css";

/* ── Date preset helpers ────────────────────────────────────── */
const now   = new Date();
const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
const last7Start = new Date(today); last7Start.setDate(today.getDate() - 7);
const last30Start = new Date(today); last30Start.setDate(today.getDate() - 30);
const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
const lastMonthEnd   = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);

const DATE_TABS = [
  { id: "today",      label: "Today",      start: today,          end: today },
  { id: "yesterday",  label: "Yesterday",  start: yesterday,      end: yesterday },
  { id: "7d",         label: "7 Days",     start: last7Start,     end: today },
  { id: "30d",        label: "30 Days",    start: last30Start,    end: today },
  { id: "this_month", label: "This Month", start: thisMonthStart, end: today },
  { id: "last_month", label: "Last Month", start: lastMonthStart, end: lastMonthEnd },
];

const fmt = (d) => dayjs(d).format("YYYY-MM-DD");

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n ?? 0);

/* ── Stat card ──────────────────────────────────────────────── */
const StatCard = ({ icon, label, value, sub, accent }) => (
  <div className={`sad-card sad-card--${accent}`}>
    <div className="sad-card__top">
      <span className="sad-card__label">{label}</span>
      <div className="sad-card__icon">{icon}</div>
    </div>
    <div className="sad-card__value">{value}</div>
    {sub && <div className="sad-card__sub">{sub}</div>}
  </div>
);

/* ── Main component ─────────────────────────────────────────── */
const SuperAdminDashboard = () => {
  const [activeTab, setActiveTab]         = useState("30d");
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [startDate, setStartDate]         = useState(fmt(last30Start));
  const [endDate, setEndDate]             = useState(fmt(today));
  const [loading, setLoading]             = useState(false);
  const [data, setData]                   = useState(null);
  const [error, setError]                 = useState(null);

  const applyTab = (tab) => {
    setActiveTab(tab.id);
    setShowCustomPicker(false);
    setStartDate(fmt(tab.start));
    setEndDate(fmt(tab.end));
  };

  const handleCustomRange = (dates) => {
    if (!dates?.[0] || !dates?.[1]) return;
    setStartDate(dates[0].format("YYYY-MM-DD"));
    setEndDate(dates[1].format("YYYY-MM-DD"));
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(
        `/admin/dashboard-stats?startDate=${startDate}&endDate=${endDate}`
      );
      setData(res.data.data);
    } catch (e) {
      console.error("SuperAdmin dashboard stats error:", e);
      setError(e?.response?.data?.message || e?.message || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  /* ── Chart configs ───────────────────────────────────────── */
  const revenueChartOptions = {
    chart: { type: "area", height: 220, toolbar: { show: false }, zoom: { enabled: false } },
    colors: ["#6366f1"],
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.55, opacityTo: 0.05 } },
    stroke: { curve: "smooth", width: 2 },
    dataLabels: { enabled: false },
    xaxis: {
      categories: data?.charts?.revenueByDay?.map((d) => d._id) || [],
      labels: { rotate: -30, style: { fontSize: "10px" } },
    },
    yaxis: { labels: { formatter: (v) => `$${(v / 100).toFixed(0)}` } },
    tooltip: { y: { formatter: (v) => fmtUSD(v / 100) } },
    grid: { borderColor: "#f0f0f0" },
    noData: { text: "No data for this period", style: { fontSize: "13px", color: "#aaa" } },
  };

  const revenueChartSeries = [{
    name: "Revenue",
    data: data?.charts?.revenueByDay?.map((d) => d.revenue) || [],
  }];

  const companiesChartOptions = {
    chart: { type: "bar", height: 220, toolbar: { show: false } },
    colors: ["#10b981"],
    dataLabels: { enabled: false },
    plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
    xaxis: {
      categories: data?.charts?.companiesByDay?.map((d) => d._id) || [],
      labels: { rotate: -30, style: { fontSize: "10px" } },
    },
    grid: { borderColor: "#f0f0f0" },
    noData: { text: "No new companies in this period", style: { fontSize: "13px", color: "#aaa" } },
  };

  const companiesChartSeries = [{
    name: "New Companies",
    data: data?.charts?.companiesByDay?.map((d) => d.count) || [],
  }];

  const pieOptions = {
    chart: { type: "donut", height: 220 },
    colors: ["#6366f1", "#f59e0b", "#ef4444", "#9ca3af"],
    labels: ["Active", "Trial", "Cancelled", "Expired"],
    dataLabels: { enabled: false },
    legend: { position: "bottom", fontSize: "12px" },
    plotOptions: { pie: { donut: { size: "65%" } } },
  };

  const pieSeries = [
    data?.subscriptions?.active   || 0,
    data?.subscriptions?.trial    || 0,
    data?.subscriptions?.cancelled || 0,
    data?.subscriptions?.expired  || 0,
  ];

  /* ── Recent invoices table ───────────────────────────────── */
  const invoiceColumns = [
    {
      title: "Company",
      dataIndex: "userId",
      render: (u) => u?.userInfo?.companyName || `${u?.firstname || ""} ${u?.lastname || ""}`.trim() || u?.email || "—",
      ellipsis: true,
    },
    {
      title: "Amount",
      dataIndex: "amountPaid",
      render: (v) => fmtUSD(v / 100),
      align: "right",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => <Tag color={s === "paid" ? "green" : s === "open" ? "orange" : "red"}>{s.toUpperCase()}</Tag>,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (d) => dayjs(d).format("DD MMM YYYY"),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">

        {/* Header */}
        <div className="card mb-3">
          <div className="card-body pb-2">
            <h4 className="page-title mb-0">Superadmin Dashboard</h4>
          </div>
        </div>

        {/* Date filter — same pattern as company admin dashboard */}
        <div className="dash-tab-group-wrapper mb-3">
          <div className="dash-tab-group">
            {DATE_TABS.map((tab) => (
              <button
                key={tab.id}
                className={`dash-tab-btn${activeTab === tab.id ? " dash-tab-btn--active" : ""}`}
                onClick={() => applyTab(tab)}
              >
                {tab.label}
              </button>
            ))}
            <button
              className={`dash-tab-btn${activeTab === "custom" ? " dash-tab-btn--active" : ""}`}
              onClick={() => { setActiveTab("custom"); setShowCustomPicker(true); }}
            >
              Custom
            </button>
            {showCustomPicker && (
              <DatePicker.RangePicker
                size="small"
                disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
                onChange={handleCustomRange}
                className="dash-custom-picker"
                allowClear={false}
              />
            )}
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <strong>Error loading stats:</strong> {error}
          </div>
        )}

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            {/* ── Row 1: Key metrics ── */}
            <div className="sad-grid sad-grid--7 mb-3">
              <StatCard
                accent="purple"
                icon={<IoBusinessOutline size={20} />}
                label="Total Companies"
                value={data?.companies?.total ?? "—"}
                sub={`+${data?.companies?.new ?? 0} in period`}
              />
              <StatCard
                accent="green"
                icon={<IoPersonAddOutline size={20} />}
                label="New Onboarded"
                value={data?.companies?.new ?? "—"}
                sub="in selected period"
              />
              <StatCard
                accent="indigo"
                icon={<IoWalletOutline size={20} />}
                label="Revenue (Period)"
                value={fmtUSD(data?.revenue?.period)}
                sub={`${data?.revenue?.periodInvoices ?? 0} invoices`}
              />
              <StatCard
                accent="teal"
                icon={<IoCardOutline size={20} />}
                label="Total Revenue"
                value={fmtUSD(data?.revenue?.total)}
                sub="all time"
              />
              <StatCard
                accent="orange"
                icon={<IoTimeOutline size={20} />}
                label="Pending Payments"
                value={fmtUSD(data?.pending?.amount)}
                sub={`${data?.pending?.count ?? 0} open invoices`}
              />
              <StatCard
                accent="red"
                icon={<IoAlertCircleOutline size={20} />}
                label="Failed Payments"
                value={fmtUSD(data?.failed?.amount)}
                sub={`${data?.failed?.count ?? 0} uncollectible`}
              />
              <StatCard
                accent="teal"
                icon={<IoWalletOutline size={20} />}
                label="Credits Recharged"
                value={fmtUSD(data?.credits?.total)}
                sub={`${data?.credits?.count ?? 0} top-ups`}
              />
            </div>

            {/* ── Row 2: Subscription status ── */}
            <div className="sad-grid sad-grid--4 mb-3">
              <StatCard
                accent="green"
                icon={<IoCheckmarkCircleOutline size={20} />}
                label="Active Subscriptions"
                value={data?.subscriptions?.active ?? "—"}
              />
              <StatCard
                accent="yellow"
                icon={<IoTimeOutline size={20} />}
                label="On Trial"
                value={data?.subscriptions?.trial ?? "—"}
              />
              <StatCard
                accent="gray"
                icon={<IoCloseCircleOutline size={20} />}
                label="Cancelled"
                value={data?.subscriptions?.cancelled ?? "—"}
              />
              <StatCard
                accent="red"
                icon={<IoAlertCircleOutline size={20} />}
                label="Expired"
                value={data?.subscriptions?.expired ?? "—"}
              />
            </div>

            {/* ── Row 3: Charts ── */}
            <div className="sad-charts-row mb-3">
              <div className="sad-chart-card">
                <div className="sad-chart-title">Revenue — {startDate} → {endDate}</div>
                <ReactApexChart
                  options={revenueChartOptions}
                  series={revenueChartSeries}
                  type="area"
                  height={220}
                />
              </div>
              <div className="sad-chart-card">
                <div className="sad-chart-title">New Companies — {startDate} → {endDate}</div>
                <ReactApexChart
                  options={companiesChartOptions}
                  series={companiesChartSeries}
                  type="bar"
                  height={220}
                />
              </div>
              <div className="sad-chart-card sad-chart-card--small">
                <div className="sad-chart-title">Subscription Breakdown</div>
                <ReactApexChart
                  options={pieOptions}
                  series={pieSeries}
                  type="donut"
                  height={220}
                />
              </div>
            </div>

            {/* ── Row 4: Recent invoices ── */}
            <div className="card">
              <div className="card-body">
                <h6 className="mb-3" style={{ fontWeight: 600 }}>Recent Paid Invoices</h6>
                <Table
                  dataSource={data?.recentInvoices || []}
                  columns={invoiceColumns}
                  rowKey="_id"
                  pagination={false}
                  size="small"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
